import {startOfToday} from 'date-fns';
import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';

import {includes, isNil, isNotNil} from 'ramda';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {CurrencyResponseBody, PaymentType, PaymentTypeSchema} from '@dms/api/shared';
import {
  GetReceiveNoteCorrectionResponse,
  GetWarehousesResponse,
  PatchCompletedReceiveNoteCorrectionRequest,
  PatchReceiveNoteCorrectionRequest,
  PostReceiveNoteCorrectionRequest,
  ReceiveNoteGeneralInfoSlice,
  ReceiveNoteStateSlice,
  usePatchCompletedReceiveNoteCorrectionMutation,
  usePatchReceiveNoteCorrectionMutation,
  usePostReceiveNoteCorrectionMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromCurrencies,
  getOptionsFromCustomersAsSupplier,
  getOptionsFromWarehouses,
  handleApiError,
  usePermissions,
} from '@dms/shared';
import {getOptionsFromPaymentTypes} from '@dms/warehouse/shared';

import {
  parseDate,
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
  getApiDateString,
} from 'shared/web';

import {
  ReceiveNoteCorrectionForm,
  ReceiveNoteCorrectionFormSchema,
} from '../types/ReceiveNoteCorrectionForm';

// The correction delivery note applies only to corrections of these receive note types.
const CORRECTION_DELIVERY_NOTE_RECEIVE_NOTE_TYPES: ReceiveNoteStateSlice['type'][] = [
  'PURCHASE',
  'OPENING_BALANCE',
];

export interface BasicInformationFormProps extends RequiredTestIdProps {
  suppliers: CustomerResponseBodyV2[] | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes: PaymentTypeResponseBody[] | Nullish;
  currencies: CurrencyResponseBody[] | Nullish;
  onClose: VoidFunction;
  receiveNoteState?: ReceiveNoteStateSlice | Nullish;
  receiveNoteGeneralInfo?: ReceiveNoteGeneralInfoSlice | Nullish;
  receiveNoteCorrection?: GetReceiveNoteCorrectionResponse | Nullish;
  sourceReceiveNoteType?: ReceiveNoteStateSlice['type'] | Nullish;
  isCreatingReceiveNoteCorrection?: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function BasicInformationForm(props: BasicInformationFormProps) {
  const navigate = useNavigate();

  const [postReceiveNoteCorrection] = usePostReceiveNoteCorrectionMutation();
  const [patchReceiveNoteCorrection] = usePatchReceiveNoteCorrectionMutation();
  const [patchCompletedReceiveNoteCorrection] = usePatchCompletedReceiveNoteCorrectionMutation();

  const [receiveNoteCorrectionEdit, receiveNoteCorrectionEditCompleted] = usePermissions({
    permissionKeys: ['receiveNoteCorrectionEdit', 'receiveNoteCorrectionEditCompleted'],
  });

  const isReceiveNoteCorrectionCompleted =
    props.receiveNoteCorrection?.creditNote?.state === 'COMPLETED';

  const canEditReceiveNoteCorrection = isReceiveNoteCorrectionCompleted
    ? receiveNoteCorrectionEditCompleted
    : receiveNoteCorrectionEdit;

  const isCorrectionDeliveryNoteEnabled = includes(
    props.sourceReceiveNoteType,
    CORRECTION_DELIVERY_NOTE_RECEIVE_NOTE_TYPES
  );

  const isReceiveNoteOpeningBalance = props.receiveNoteState?.type === 'OPENING_BALANCE';

  const isReceiveNoteCorrectionDraft =
    props.receiveNoteCorrection?.creditNote?.state === 'PENDING' ||
    props.isCreatingReceiveNoteCorrection;

  const isSupplierIdEnabled = isReceiveNoteOpeningBalance && isReceiveNoteCorrectionDraft;

  const defaultValues: Partial<ReceiveNoteCorrectionForm> = props.isCreatingReceiveNoteCorrection
    ? {
        supplierId: props.receiveNoteGeneralInfo?.supplierId,
        warehouseId: props.receiveNoteGeneralInfo?.warehouseId,
        authorizationProfileId: props.receiveNoteGeneralInfo?.authorizationProfileId,
        paymentType: props.receiveNoteGeneralInfo?.paymentType,
        currency: props.receiveNoteGeneralInfo?.currency,
        exchangeRate: props.receiveNoteGeneralInfo?.exchangeRate,
        ncConversion: props.receiveNoteGeneralInfo?.ncConversion,
        note: props.receiveNoteGeneralInfo?.note,
      }
    : {
        supplierId: props.receiveNoteCorrection?.creditNote?.supplierId,
        warehouseId: props.receiveNoteCorrection?.creditNote?.warehouseId,
        authorizationProfileId: props.receiveNoteCorrection?.creditNote?.authorizationProfileId,
        correctionDeliveryNoteNumber:
          props.receiveNoteCorrection?.creditNote?.correctionDeliveryNoteNumber,
        correctionDeliveryNoteDate: parseDate(
          props.receiveNoteCorrection?.creditNote?.correctionDeliveryNoteDate
        ),
        invoiceNumber: props.receiveNoteCorrection?.creditNote?.correctionInvoiceNumber,
        invoiceIssueDate: parseDate(props.receiveNoteCorrection?.creditNote?.correctionInvoiceDate),
        paymentType: props.receiveNoteCorrection?.creditNote?.paymentType,
        currency: props.receiveNoteCorrection?.creditNote?.currency,
        exchangeRate: props.receiveNoteCorrection?.creditNote?.exchangeRate,
        ncConversion: props.receiveNoteCorrection?.creditNote?.ncConversion,
        note: props.receiveNoteCorrection?.creditNote?.note,
      };

  const handleSubmit: FormSubmitHandler<ReceiveNoteCorrectionForm> = async (formValues) => {
    const correctionDeliveryNote = isCorrectionDeliveryNoteEnabled
      ? {
          correctionDeliveryNoteNumber: formValues.correctionDeliveryNoteNumber,
          correctionDeliveryNoteDate: isNotNil(formValues.correctionDeliveryNoteDate)
            ? getApiDateString(formValues.correctionDeliveryNoteDate)
            : null,
        }
      : {};

    if (props.isCreatingReceiveNoteCorrection) {
      const requestBody: PostReceiveNoteCorrectionRequest['body'] = {
        // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
        receiveNoteId: props.receiveNoteState?.receiveNoteId!,
        ...correctionDeliveryNote,
        supplierId: formValues.supplierId,
        warehouseId: formValues.warehouseId,
        correctionInvoiceNumber: formValues.invoiceNumber,
        invoiceIssueDate: isNotNil(formValues.invoiceIssueDate)
          ? getApiDateString(formValues.invoiceIssueDate)
          : null,
        paymentType: formValues.paymentType.toLowerCase() as PaymentType,
        currency: formValues.currency,
        exchangeRate: formValues.exchangeRate,
        ncConversion: formValues.ncConversion,
        note: formValues.note,
      };

      return await postReceiveNoteCorrection({
        body: requestBody,
      })
        .unwrap()
        .then((receiveNoteCorrection) => {
          showNotification.success(
            i18n.t('entity.warehouse.notifications.receiveNoteCorrectionCreated')
          );
          props.onClose();
          navigate(
            composePath(warehouseRoutes.receiveNoteCorrectionDetailOverview, {
              params: {id: receiveNoteCorrection.id},
            })
          );
        })
        .catch(handleApiError);
    }

    const creditNoteId = props.receiveNoteCorrection?.creditNote?.creditNoteId;

    if (isReceiveNoteCorrectionCompleted) {
      if (isNil(creditNoteId)) {
        return;
      }

      const completedRequestBody: PatchCompletedReceiveNoteCorrectionRequest['body'] = {
        ...correctionDeliveryNote,
        correctionInvoiceNumber: formValues.invoiceNumber,
        invoiceIssueDate: isNotNil(formValues.invoiceIssueDate)
          ? getApiDateString(formValues.invoiceIssueDate)
          : null,
        paymentType: PaymentTypeSchema.parse(formValues.paymentType.toLowerCase()),
        note: formValues.note,
      };

      return await patchCompletedReceiveNoteCorrection({
        creditNoteId,
        body: completedRequestBody,
      })
        .unwrap()
        .then(props.onClose)
        .catch(handleApiError);
    }

    const requestBody: PatchReceiveNoteCorrectionRequest['body'] = {
      ...correctionDeliveryNote,
      supplierId: formValues.supplierId,
      warehouseId: formValues.warehouseId,
      correctionInvoiceNumber: formValues.invoiceNumber,
      invoiceIssueDate: isNotNil(formValues.invoiceIssueDate)
        ? getApiDateString(formValues.invoiceIssueDate)
        : null,
      paymentType: formValues.paymentType.toLowerCase() as PaymentType,
      currency: formValues.currency,
      exchangeRate: formValues.exchangeRate,
      ncConversion: formValues.ncConversion,
      note: formValues.note,
    };

    await patchReceiveNoteCorrection({
      // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
      creditNoteId: props.receiveNoteCorrection?.creditNote?.creditNoteId!,
      body: requestBody,
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  return (
    <Form<ReceiveNoteCorrectionForm>
      experimentalZodSchema={ReceiveNoteCorrectionFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      isDisabled={!canEditReceiveNoteCorrection && !props.isCreatingReceiveNoteCorrection}
    >
      {(control, formApi) => (
        <>
          <VStack spacing={4}>
            <Grid columns={4}>
              <FormField
                isRequired
                isNotClearable
                isDisabled={!isSupplierIdEnabled}
                control={control}
                type="choice"
                name="supplierId"
                label={i18n.t('entity.warehouse.labels.supplier')}
                options={getOptionsFromCustomersAsSupplier(props.suppliers)}
                data-testid={suffixTestId('inputs.supplierId', props)}
              />
              <FormField
                isRequired
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="warehouseId"
                label={i18n.t('entity.warehouse.labels.warehouse')}
                options={getOptionsFromWarehouses(props.warehouses)}
                data-testid={suffixTestId('inputs.warehouseId', props)}
              />
              <FormField
                isRequired
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="authorizationProfileId"
                label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                options={getOptionsFromAuthorizationProfiles(
                  props.authorizationProfiles,
                  formApi.watch('authorizationProfileId')
                )}
                data-testid={suffixTestId('inputs.authorizationProfileId', props)}
              />
            </Grid>

            <Grid columns={4}>
              <FormField
                isDisabled={!isCorrectionDeliveryNoteEnabled}
                control={control}
                type="text"
                name="correctionDeliveryNoteNumber"
                label={i18n.t('entity.warehouse.labels.deliveryNoteNumber')}
                data-testid={suffixTestId('inputs.correctionDeliveryNoteNumber', props)}
              />
              <FormField
                isDisabled={!isCorrectionDeliveryNoteEnabled}
                control={control}
                type="date"
                name="correctionDeliveryNoteDate"
                maxDate={startOfToday()}
                label={i18n.t('entity.warehouse.labels.deliveryNoteIssueDate')}
                data-testid={suffixTestId('inputs.correctionDeliveryNoteDate', props)}
              />
              <FormField
                control={control}
                type="text"
                name="invoiceNumber"
                label={i18n.t('entity.warehouse.labels.invoiceNumber')}
                data-testid={suffixTestId('inputs.invoiceNumber', props)}
              />
              <FormField
                control={control}
                type="date"
                name="invoiceIssueDate"
                maxDate={startOfToday()}
                label={i18n.t('entity.warehouse.labels.invoiceIssueDate')}
                data-testid={suffixTestId('inputs.invoiceIssueDate', props)}
              />
            </Grid>

            <Grid columns={4}>
              <FormField
                isRequired
                isNotClearable
                control={control}
                type="choice"
                name="paymentType"
                label={i18n.t('entity.warehouse.labels.paymentType')}
                options={getOptionsFromPaymentTypes(props.paymentTypes)}
                data-testid={suffixTestId('inputs.paymentType', props)}
              />
              <FormField
                isRequired
                isDisabled
                control={control}
                type="choice"
                name="currency"
                label={i18n.t('entity.warehouse.labels.currency')}
                options={getOptionsFromCurrencies(props.currencies)}
                data-testid={suffixTestId('inputs.currency', props)}
              />
              <FormField
                isDisabled={isReceiveNoteCorrectionCompleted}
                control={control}
                type="number"
                name="exchangeRate"
                label={i18n.t('entity.warehouse.labels.exchangeRate')}
                data-testid={suffixTestId('inputs.exchangeRate', props)}
              />
              <FormField
                isDisabled={isReceiveNoteCorrectionCompleted}
                control={control}
                type="choice"
                name="ncConversion"
                label={i18n.t('entity.warehouse.labels.ncConversion')}
                options={[]}
                data-testid={suffixTestId('inputs.ncConversion', props)}
              />
            </Grid>

            <Grid columns={4}>
              <GridItem span={4}>
                <FormField
                  control={control}
                  type="textarea"
                  name="note"
                  label={i18n.t('entity.warehouse.labels.note')}
                  data-testid={suffixTestId('inputs.note', props)}
                />
              </GridItem>
            </Grid>

            <HStack justify="flex-end">
              <ButtonGroup>
                <Show when={isNotNil(props.receiveNoteCorrection)}>
                  <FormButton
                    control={control}
                    type="reset"
                    variant="secondary"
                    title={i18n.t('general.labels.discard')}
                    onClick={props.onClose}
                    data-testid={suffixTestId('actions.discard', props)}
                  />
                </Show>
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('general.labels.saveChanges')}
                  data-testid={suffixTestId('actions.submit', props)}
                />
              </ButtonGroup>
            </HStack>
          </VStack>
        </>
      )}
    </Form>
  );
}
